import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { ExtraOptions, ICoreConfig, IDrawingAssets, ISvgScales, SVGComponents, SvgNode } from '../core/culture-index-chart.model'
import { easeLinearAnimation, getGroupClassNames, getOneOfDotsType } from '../utils/d3-utils'

type DrawConnectionsArgs = ISvgScales & IDrawingAssets & ExtraOptions
@Component({
  selector: 'culture-index-chart-connections',
  standalone: true,
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CultureIndexChartConnectionsComponent {
  @Input({ required: true }) coreConfig!: ICoreConfig

  /**
   * Draws connection / links and connects nodes together with dashed or solid line
   */
  drawConnections(svg: SvgNode, args: DrawConnectionsArgs, loading?: boolean) {
    const { xScale, yScale, connections, nodes, compare } = args
    const linksGroup = svg.append('g').attr('class', `${getOneOfDotsType(compare)}${SVGComponents.LINKS}`)

    connections.forEach((connection, i) => {
      const source = nodes.find(n => n.id === connection.source)
      const target = nodes.find(n => n.id === connection.target)

      if (!source || !target) return
      linksGroup
        .append('line')
        .attr('x1', xScale(source.x + 2 + 0.08))
        .attr('y1', yScale(source.y + 0.25))
        .attr('stroke', () => (loading ? '#e0e0e0' : connection.color || '#000'))
        .attr('stroke-width', connection.width || 1.2)
        .attr('stroke-dasharray', () => {
          if (loading) return '5,0'
          return compare ? '2,2' : '5,0'
        })
        .attr('x2', xScale(source.x + 2 + 0.08))
        .attr('y2', yScale(source.y + 0.25))
        .style('opacity', 0)
        .transition()
        .duration(300)
        .delay(400)
        .transition()
        .duration(300)
        .delay(i * 400)
        .ease(easeLinearAnimation)
        .attr('x2', xScale(target.x + 2 + 0.08))
        .attr('y2', yScale(target.y + 0.25))
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1)
    })
  }

  /**
   * Updates connection / links and connects nodes together with dashed or solid line
   */
  updateConnections(svg: SvgNode, args: DrawConnectionsArgs, loading?: boolean) {
    const { xScale, yScale, connections, nodes, compare } = args
    const group = getGroupClassNames([SVGComponents.LINKS], [getOneOfDotsType(compare)])[0]

    const links = svg.selectAll(`${group} > line`)

    if (links.size() == 0) {
      this.drawConnections(svg, args, loading)
      return
    }

    links
      .data(connections)
      .transition()
      .duration(500)
      .attr('x1', connection => {
        const source = nodes.find(n => n.id === connection.source)
        return source ? xScale(source.x + 2 + 0.08) : null
      })
      .attr('y1', connection => {
        const source = nodes.find(n => n.id === connection.source)
        return source ? yScale(source.y + 0.25) : null
      })
      .attr('x2', connection => {
        const target = nodes.find(n => n.id === connection.target)
        return target ? xScale(target.x + 2 + 0.08) : null
      })
      .attr('y2', connection => {
        const target = nodes.find(n => n.id === connection.target)
        return target ? yScale(target.y + 0.25) : null
      })
      .attr('stroke', connection => (loading ? '#e0e0e0' : connection.color || '#000'))
      .attr('stroke-width', connection => connection.width || 1.2)
      .attr('stroke-dasharray', () => {
        if (loading) return '5,0'
        return compare ? '2,2' : '5,0'
      })
      .style('opacity', 1)
  }
}
